import { useMemo } from 'react'
import { Eye, TrendingUp } from 'lucide-react'
import { useLocation  } from 'react-router-dom'

import { useSiteData } from '../../hooks/useSiteData'
import * as styled from '../../../../util/styled'
import { ResultLayout } from './ResultLayout'
import { SearchWebAndNewsQuery } from '../../../../gql/graphql'
import { ImageTile } from '../../../../design-system/ImageTile'
import { humanizeNumber } from '../../../../util/humanizeNumber'

const Description = styled.paragraph('line-clamp-2')
const AddJournalist = styled.button('')
const DomainWrapper = styled.div('flex flex-row gap-2 items-center')
const DomainImg = styled.img('w-5 h-5')
const DomainLink = styled.anchor('text-brand text-sm block')

type WebAndNewsProps = {
  result: SearchWebAndNewsQuery['searchWebAndNews']['items'][number]
}

export const WebAndNewsResult = (props: WebAndNewsProps) => {
  const imageUri = props.result.thumbnail ?? `/util/scraper/og-image?url=${encodeURIComponent(props.result.url)}`
  const location = useLocation()
  const domain = useMemo(() => {
    const url = new URL(props.result.url)
    return (url.hostname ?? '').replace('www.', '')
  }, [props.result.url])
  const clearbitFavicon = `https://logo.clearbit.com/${encodeURIComponent(domain)}`
  const urlWithDomainFilter = useMemo(() => {
    // update q to include domain
    const search = new URLSearchParams(location.search)
    search.set('q', `${search.get('q')} ${domain}`)
    return `${location.pathname}?${search.toString()}`
  }, [location])
  const siteData = useSiteData(domain)
  const analyticsTooltip = siteData?.dailyVisitors && siteData?.alexaRank ? `${siteData?.dailyVisitors} daily visitors and an Alexa rank of ${siteData?.alexaRank}` : `No data available for ${domain}`
  return (
    <ResultLayout
      image={<ImageTile src={imageUri} alt={props.result.title } />}
      domain={
        <DomainWrapper>
          <DomainImg src={clearbitFavicon} />
          <DomainLink href={urlWithDomainFilter}>{domain}</DomainLink>
        </DomainWrapper>
      }
      title={props.result.title ?? ''}
      titleUrl={props.result.url}
      description={<Description>{props.result.description}</Description>}
      analyticsTooltip={analyticsTooltip}
      analytics={[
        { key: 'dailyVisitors', title: <Eye className='w-4 h-4' />, value: humanizeNumber(siteData?.dailyVisitors) },
        { key: 'alexaRank', title: <TrendingUp className='w-4 h-4' />, value: humanizeNumber(siteData?.alexaRank) },
      ]}
      cta={<AddJournalist>Add Journalist</AddJournalist>}
    />
  )
}
